import React from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { appTheme } from "shared/theme";
import { useNavigate } from "react-router-dom";

export const VisionContent = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isIpadLandscape = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const viewFeatures = () => {
    navigate("/product/features");
  };

  const viewTeam = () => {
    navigate("/team");
  };

  const StoreBadges = ({ center }) => (
    <Box
      sx={{
        display: "flex",
        gap: 1.25,
        justifyContent: center ? "center" : "flex-start",
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <Box
        component="a"
        href="https://apps.apple.com/au/app/7day-app/id6470282265?itsct=apps_box_link&itscg=30200"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          lineHeight: 0,
          display: "inline-flex",
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "transparent",
        }}
      >
        <Box
          component="img"
          alt="Download on the App Store"
          src="/thirdParty/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
          sx={{
            height: { xs: 44, sm: 50, md: 44 },
            width: "auto",
            display: "block",
          }}
        />
      </Box>

      <Box
        component="a"
        href="https://play.google.com/store/apps/details?id=com.sevenday.mobile&pcampaignid=web_share7DAY"
        target="_blank"
        rel="noopener noreferrer"
        sx={{ lineHeight: 0, display: "inline-flex" }}
      >
        <Box
          component="img"
          alt="Get it on Google Play"
          src="/thirdParty/GetItOnGooglePlay_Badge_Web_color_English.png"
          sx={{
            height: { xs: 44, sm: 50, md: 44 },
            width: "auto",
            display: "block",
          }}
        />
      </Box>
    </Box>
  );

  const ValuePill = ({ title, body }) => (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid rgba(20,55,74,0.10)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.86) 0%, rgba(255,255,255,0.72) 100%)",
        backdropFilter: "blur(10px)",
        px: 2.2,
        py: 1.8,
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontFamily:
            appTheme.typography?.h1?.fontFamily ||
            appTheme.typography?.body1?.fontFamily,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: appTheme.palette.blue.main,
          fontSize: 12,
          mb: 0.9,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: appTheme.typography?.body1?.fontFamily,
          color: appTheme.palette.gray.main,
          lineHeight: 1.7,
          fontSize: 14,
        }}
      >
        {body}
      </Typography>
    </Box>
  );

  return (
    <Box
      id="vision"
      sx={{
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        ml: "-50vw",
        mr: "-50vw",
        overflow: "hidden",
        backgroundColor: appTheme.palette.white.main,
        minHeight: "100svh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: 10, sm: 12, md: 12 },
          pb: { xs: 8, sm: 9, md: isIpadLandscape ? 7 : 9 },
          background: {
            xs: appTheme.palette.blue.main,
            md: "radial-gradient(900px 520px at 10% 0%, rgba(92,181,179,0.22) 0%, rgba(255,255,255,0) 60%), radial-gradient(900px 520px at 100% 10%, rgba(0,0,0,0.05) 0%, rgba(255,255,255,0) 55%)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "#F9F4F3",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: {
              xs: `linear-gradient(180deg, ${appTheme.palette.blue.main} 0%, ${appTheme.palette.blue.main} 62%, rgba(209,235,233,1) 62%, rgba(209,235,233,1) 100%)`,
              md: `linear-gradient(90deg, ${appTheme.palette.blue.main} 0%, ${appTheme.palette.blue.main} 58%, rgba(209,235,233,1) 58%, rgba(209,235,233,1) 100%)`,
            },
            zIndex: 1,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: {
              xs: `radial-gradient(circle at 20% 20%, rgba(20,55,74,0.12) 0%, rgba(20,55,74,0) 55%)`,
              md: `radial-gradient(circle at 18% 50%, rgba(20,55,74,0.12) 0%, rgba(20,55,74,0) 55%)`,
            },
            zIndex: 2,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 3 }}>
          <Grid
            container
            spacing={{ xs: 3, md: 5 }}
            alignItems="center"
            sx={{
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Grid item xs={12} md={6}>
              <Box sx={{ pr: { xs: 0, md: 2 } }}>
                <Typography
                  sx={{
                    color: appTheme.palette.yellow.main,
                    fontFamily: appTheme.typography?.body1?.fontFamily,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    fontWeight: 800,
                    fontSize: { xs: 12, sm: 13, md: 13 },
                    mb: 1.5,
                  }}
                >
                  We are offering businesses the ultimate work perk!
                </Typography>

                <Typography
                  sx={{
                    fontFamily:
                      appTheme.typography?.h1?.fontFamily ||
                      appTheme.typography?.body1?.fontFamily,
                    textTransform: "uppercase",
                    fontWeight: 900,
                    letterSpacing: "0.05em",
                    color: appTheme.palette.white.main,
                    lineHeight: 1.05,
                    fontSize: { xs: 30, sm: 40, md: 42, lg: 58 },
                    mb: 2,
                  }}
                >
                  One platform.
                  <br />
                  Countless experiences.
                  <br />
                  All for work.
                  <br />
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      mt: 0.75,
                      color: appTheme.palette.yellow.main,
                      fontFamily: appTheme.typography?.body1?.fontFamily,
                      fontWeight: 800,
                      fontSize: { xs: 12, sm: 13, md: 13 },
                    }}
                  >
                    Or not
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: 14.5, sm: 15.5, md: 15.5 },
                    color: "rgba(255,255,255,0.9)",
                    lineHeight: 1.8,
                    maxWidth: { xs: "100%", md: 520 },
                    mx: { xs: "auto", md: 0 },
                    mb: { xs: 3, md: isIpadLandscape ? 2.5 : 4 },
                  }}
                >
                  Reward teams with standout stays, offsites, retreats, boats,
                  venues, vehicles, and experiences—booked in one place.
                </Typography>

                <Box
                  sx={{
                    mt: 2.2,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.4,
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: { xs: "center", md: "flex-start" },
                    maxWidth: { xs: "100%", sm: 520 },
                  }}
                >
                  <Button
                    onClick={viewFeatures}
                    sx={{
                      px: 2.8,
                      py: 1.2,
                      borderRadius: 3,
                      backgroundColor: appTheme.palette.yellow?.main,
                      color: appTheme.palette.white?.main,
                      fontWeight: 900,
                      letterSpacing: ".10rem",
                      textTransform: "uppercase",
                      "&:hover": {
                        opacity: 0.9,
                        backgroundColor: appTheme.palette.white?.main,
                        color: appTheme.palette.blue?.main,
                      },
                    }}
                  >
                    Features
                  </Button>

                  <Box
                    sx={{
                      display: { xs: "none", sm: "flex" },
                      alignItems: "center",
                      justifyContent: { xs: "center", sm: "flex-start" },
                      gap: 1.25,
                    }}
                  >
                    <StoreBadges center={!mdUp} />
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                  alignItems: "center",
                  pt: { xs: 1, md: 0 },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: -10, md: "12%" },
                    right: { xs: "auto", md: "10%" },
                    width: {
                      xs: 260,
                      sm: 320,
                      md: isIpadLandscape ? 320 : 360,
                    },
                    height: {
                      xs: 260,
                      sm: 320,
                      md: isIpadLandscape ? 320 : 360,
                    },
                    borderRadius: "50%",
                    background: "rgba(20,55,74,0.10)",
                    filter: "blur(2px)",
                  }}
                />

                <Box
                  sx={{
                    width: { xs: "92%", sm: "78%", md: "100%" },
                    maxWidth: {
                      md: isIpadLandscape ? 520 : 560,
                      lg: 620,
                      xl: 720,
                    },
                    borderRadius: { xs: 5, md: 6 },
                    overflow: "hidden",
                    boxShadow: "0 20px 60px rgba(20,55,74,0.18)",
                    backgroundColor: "rgba(255,255,255,0.35)",
                    border: "1px solid rgba(255,255,255,0.45)",
                    backdropFilter: "blur(10px)",
                    px: { xs: 2, sm: 3, md: 3.5 },
                    py: { xs: 2, sm: 3, md: 3.5 },
                  }}
                >
                  <Box
                    component="img"
                    src="/imgs/sitegraphics/app2.png"
                    alt="app"
                    sx={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: { xs: 4, md: 0 },
                      maxHeight: {
                        xs: 340,
                        sm: 380,
                        md: isIpadLandscape ? 420 : 520,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          background:
            "radial-gradient(900px 520px at 50% 0%, rgba(92,181,179,0.10) 0%, rgba(255,255,255,0) 55%)",
          py: { xs: 7, md: isIpadLandscape ? 6 : 9 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                <Typography
                  sx={{
                    fontSize: { xs: 26, sm: 32, md: 38 },
                    fontWeight: 900,
                    fontFamily:
                      appTheme.typography?.h1?.fontFamily ||
                      appTheme.typography?.body1?.fontFamily,
                    color: appTheme.palette.blue.main,
                    lineHeight: 1.15,
                    mb: 1.6,
                  }}
                >
                  The power of culture makes a difference
                </Typography>

                <Box
                  sx={{
                    mx: { xs: "auto", md: 0 },
                    mb: 2.8,
                    height: 1,
                    width: { xs: "100%", sm: 520 },
                    backgroundColor: "rgba(0,0,0,0.08)",
                  }}
                />

                <Typography
                  sx={{
                    fontSize: { xs: 15.5, md: 16.5 },
                    color: appTheme.palette.blue.main,
                    lineHeight: 1.9,
                    mb: 2.2,
                    opacity: 0.92,
                    maxWidth: 760,
                    mx: { xs: "auto", md: 0 },
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  7DAY is a marketplace reimagining how companies reward and
                  recharge their teams. We connect businesses with unique spaces
                  and experiences—think houses, boats, retreats, office venues,
                  vehicles, and activities—making it easy to plan everything
                  from offsites to personal getaways in one place.
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: 15.5, md: 16.5 },
                    color: appTheme.palette.blue.main,
                    lineHeight: 1.9,
                    opacity: 0.92,
                    maxWidth: 760,
                    mx: { xs: "auto", md: 0 },
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  Say goodbye to the traditional 9–5. Fuel your team's energy
                  with inspiring experiences and flexible locations that help
                  your culture thrive—and your business shine.
                </Typography>

                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.4,
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: { xs: "center", md: "flex-start" },
                  }}
                >
                  <Button
                    onClick={viewTeam}
                    variant="outlined"
                    sx={{
                      px: 2.8,
                      py: 1.1,
                      borderRadius: 3,
                      borderColor: "rgba(20,55,74,0.18)",
                      color: appTheme.palette.blue.main,
                      fontWeight: 900,
                      letterSpacing: ".08rem",
                      textTransform: "uppercase",
                      backgroundColor: "rgba(255,255,255,0.72)",
                      "&:hover": {
                        borderColor: appTheme.palette.blue.main,
                        backgroundColor: appTheme.palette.blue.main,
                        color: appTheme.palette.white.main,
                      },
                    }}
                  >
                    Meet the team
                  </Button>
                </Box>

                <Box
                  sx={{
                    mt: 3.2,
                    display: { xs: "flex", sm: "none" },
                    justifyContent: "center",
                  }}
                >
                  <StoreBadges center />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <ValuePill
                  title="For Businesses"
                  body="Reward teams with experiences that actually land—without the admin headache."
                />

                <ValuePill
                  title="For Hosts"
                  body="Get discovered by teams and guests searching for standout stays and venues."
                />

                <ValuePill
                  title="For Contractors"
                  body="List once, get booked for services and add-ons across multiple experiences."
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};